import React from "react";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import { formatWaypointLabel } from "../routeMarkers";
import { FavoriteAddress, GeocodeSuggestion, OrderFormLocale, RouteWaypoint } from "../types";
import styles from "./orderForm.module.scss";

type RouteInputsProps = {
  locale: OrderFormLocale;
  pickup: string;
  dropoff: string;
  waypoints: RouteWaypoint[];
  maxWaypoints: number;
  favoriteAddresses?: FavoriteAddress[];
  onPickupChange: (value: string) => void;
  onDropoffChange: (value: string) => void;
  onPickupSelect: (suggestion: GeocodeSuggestion) => void;
  onDropoffSelect: (suggestion: GeocodeSuggestion) => void;
  onPickupCoordsClear: () => void;
  onDropoffCoordsClear: () => void;
  onPickupAddOnMap: () => void;
  onDropoffAddOnMap: () => void;
  onWaypointChange: (waypointId: string, value: string) => void;
  onWaypointSelect: (waypointId: string, suggestion: GeocodeSuggestion) => void;
  onWaypointCoordsClear: (waypointId: string) => void;
  onWaypointAddOnMap: (waypointId: string) => void;
  onWaypointRemove: (waypointId: string) => void;
  onAddWaypoint: () => void;
  onSwap: () => void;
};

const SwapAddressesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M5 2v12M5 14l-3-3M5 14l3-3M11 14V2M11 2l3 3M11 2L8 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RouteInputs: React.FC<RouteInputsProps> = ({
  locale,
  pickup,
  dropoff,
  waypoints,
  maxWaypoints,
  favoriteAddresses = [],
  onPickupChange,
  onDropoffChange,
  onPickupSelect,
  onDropoffSelect,
  onPickupCoordsClear,
  onDropoffCoordsClear,
  onPickupAddOnMap,
  onDropoffAddOnMap,
  onWaypointChange,
  onWaypointSelect,
  onWaypointCoordsClear,
  onWaypointAddOnMap,
  onWaypointRemove,
  onAddWaypoint,
  onSwap,
}) => (
  <div className={styles.routeWrap}>
      <div className={styles.routeLine} aria-hidden>
        <span className={styles.routeDot} />
        <span className={styles.routeLineBar} />
        <span className={styles.routeArrow} />
      </div>

      <div className={styles.routeAddressFields}>
        <AddressAutocompleteInput
          prefix={locale.route.from}
          value={pickup}
          placeholder={locale.route.fromPlaceholder}
          loadingLabel={locale.route.loading}
          emptyLabel={locale.route.noResults}
          addOnMapLabel={locale.route.addOnMap}
          favoriteSectionLabel={locale.route.favoriteAddressesSection}
          searchResultsSectionLabel={locale.route.searchResultsSection}
          favoriteAddresses={favoriteAddresses}
          onChange={onPickupChange}
          onSelect={onPickupSelect}
          onCoordsClear={onPickupCoordsClear}
          onAddOnMap={onPickupAddOnMap}
          showClear
          clearAriaLabel={locale.route.clearAddressAria}
        />

        {waypoints.map((waypoint, index) => (
          <div key={waypoint.id} className={styles.routeWaypointRow}>
            <AddressAutocompleteInput
              prefix={formatWaypointLabel(locale.route.waypointLabel, index + 1)}
              value={waypoint.address}
              placeholder={locale.route.waypointPlaceholder}
              loadingLabel={locale.route.loading}
              emptyLabel={locale.route.noResults}
              addOnMapLabel={locale.route.addOnMap}
              favoriteSectionLabel={locale.route.favoriteAddressesSection}
              searchResultsSectionLabel={locale.route.searchResultsSection}
              favoriteAddresses={favoriteAddresses}
              onChange={(value) => onWaypointChange(waypoint.id, value)}
              onSelect={(suggestion) => onWaypointSelect(waypoint.id, suggestion)}
              onCoordsClear={() => onWaypointCoordsClear(waypoint.id)}
              onAddOnMap={() => onWaypointAddOnMap(waypoint.id)}
            />
            <button
              type="button"
              className={styles.routeRemoveWaypointBtn}
              onClick={() => onWaypointRemove(waypoint.id)}
              aria-label={locale.route.removeWaypointAria}
            >
              ×
            </button>
          </div>
        ))}

        <AddressAutocompleteInput
          prefix={locale.route.to}
          value={dropoff}
          placeholder={locale.route.toPlaceholder}
          loadingLabel={locale.route.loading}
          emptyLabel={locale.route.noResults}
          addOnMapLabel={locale.route.addOnMap}
          favoriteSectionLabel={locale.route.favoriteAddressesSection}
          searchResultsSectionLabel={locale.route.searchResultsSection}
          favoriteAddresses={favoriteAddresses}
          onChange={onDropoffChange}
          onSelect={onDropoffSelect}
          onCoordsClear={onDropoffCoordsClear}
          onAddOnMap={onDropoffAddOnMap}
          showClear
          clearAriaLabel={locale.route.clearAddressAria}
        />
      </div>

      <div className={styles.routeActions}>
        {waypoints.length < maxWaypoints && (
          <button type="button" className={styles.routeAddWaypointBtn} onClick={onAddWaypoint}>
            <span className={styles.routeAddWaypointIcon} aria-hidden>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 14.667s4.667-5.227 4.667-8A4.667 4.667 0 1 0 3.333 6.667c0 2.773 4.667 8 4.667 8z"
                  fill="currentColor"
                />
                <circle cx="8" cy="6.667" r="1.5" fill="#fff" />
              </svg>
            </span>
            {locale.route.addWaypoint}
          </button>
        )}

        <button type="button" className={styles.routeSwapBtn} onClick={onSwap} aria-label={locale.route.swapAria}>
          <SwapAddressesIcon className={styles.routeSwapIcon} />
        </button>
      </div>
    </div>
);

export default RouteInputs;
